/**
 * ThumbnailService — produces the 240px filmstrip thumbnail for a photo.
 * Single job. Previews are PreviewService's problem.
 */

import { cacheKey } from '../domain/cache-key';
import { THUMB_PX } from '../domain/preview-size';
import { FileMissingError } from '../adapters/fs-port';
import type { BlobStore } from '../adapters/opfs-store';
import type { ImageProcessor } from '../adapters/image-processor';
import type { MediaRepository } from '../repositories';
import type { MediaItem } from '../types';
import type { ChangeSink, Clock, DirectoryResolver } from './ports';

const THUMB_QUALITY = 0.72;

export class ThumbnailService {
  /**
   * Generations already under way, keyed by media id.
   *
   * Review Assist walks the whole shoot while the filmstrip generates whatever
   * scrolls into view, so both routinely ask for the same thumbnail at the same
   * moment. Without this, two writers race on one OPFS file and whichever reader
   * is mid-read fails with ERR_UPLOAD_FILE_CHANGED. Sharing one promise makes
   * the second caller wait for the first rather than duplicating the work.
   */
  private readonly inFlight = new Map<number, Promise<string | null>>();

  constructor(
    private readonly media: MediaRepository,
    private readonly cache: BlobStore,
    private readonly images: ImageProcessor,
    private readonly resolve: DirectoryResolver,
    private readonly sink: ChangeSink,
    private readonly clock: Clock,
  ) {}

  /** Generates and stores one thumbnail; returns its cache key, or null on failure. */
  generate(item: MediaItem): Promise<string | null> {
    const existing = this.inFlight.get(item.id);
    if (existing) return existing;

    const work = this.generateOnce(item).finally(() => this.inFlight.delete(item.id));
    this.inFlight.set(item.id, work);
    return work;
  }

  private async generateOnce(item: MediaItem): Promise<string | null> {
    if (item.sourceId === null || item.type !== 'image') return null;

    const key = cacheKey({
      sourceId: item.sourceId,
      relPath: item.relPath,
      sizeBytes: item.sizeBytes,
      modifiedAt: item.modifiedAt,
      kind: 'thumb',
      targetPx: THUMB_PX,
    });

    if (await this.cache.has(key)) {
      this.media.setThumbKey(item.id, key);
      this.sink.touch();
      return key;
    }

    const directory = await this.resolve(item.sourceId);
    if (!directory) return null;

    try {
      const file = await directory.file(item.relPath);
      const resized = await this.images.resize(await file.blob(), THUMB_PX, THUMB_QUALITY);

      await this.cache.put(key, resized.bytes);
      this.media.setThumbKey(item.id, key);
      this.media.setDimensions(item.id, resized.sourceWidth, resized.sourceHeight);
      this.media.setStatus(item.id, 'ok', this.clock());
      this.sink.touch();
      return key;
    } catch (err) {
      const status = err instanceof FileMissingError ? 'missing' : 'error';
      this.media.setStatus(item.id, status, this.clock());
      this.sink.touch();
      return null;
    }
  }

  /**
   * Works through everything still missing a thumbnail, a few at a time.
   * Bounded concurrency keeps the main thread responsive while culling continues.
   */
  async generateMissing(
    projectId: number,
    options: {
      batch?: number;
      concurrency?: number;
      signal?: { aborted: boolean };
      onProgress?: (done: number, total: number) => void;
    } = {},
  ): Promise<number> {
    const batch = options.batch ?? 5000;
    const concurrency = Math.max(1, options.concurrency ?? 4);

    const queue = this.media.missingThumbs(projectId, batch);
    if (queue.length === 0) return 0;

    let done = 0;
    let cursor = 0;

    const worker = async (): Promise<void> => {
      while (cursor < queue.length && !options.signal?.aborted) {
        const item = queue[cursor++];
        await this.generate(item);
        done++;
        options.onProgress?.(done, queue.length);
      }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
    return done;
  }
}
